import { getDateWithTimezoneFixed, readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { differenceInCalendarDays } from 'date-fns';
import _ from 'lodash';
import { quickScore } from 'quick-score';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TEventType } from '../../entities/events/queries';
import { TManagerAccount } from '../../entities/users/queries';
import { TEventTypes } from '../../generated/graphql';

export const generateMonthFilter = (d: Date | string) => {
  return readableDate(d, 'yyyy-MM');
};

type TUseEventsFiltersProps = {
  events: TEventType[];
};

export const useEventsFilters = (props: TUseEventsFiltersProps) => {
  const { events } = props;

  const [monthFilter, setMonthFilter] = useState<string>();
  const [companyFilter, setCompanyFilter] = useState<string>();
  const [officeFilter, setOfficeFilter] = useState<string>();
  const [recipientNameFilter, setRecipientNameFilter] = useState<string>();

  // Debounced so a server-side name search (see useSearchHomeEventsByRecipient) doesn't fire a
  // request on every keystroke -- waits for a 300ms pause in typing.
  const [debouncedRecipientNameFilter, setDebouncedRecipientNameFilter] = useState<string>();

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedRecipientNameFilter(recipientNameFilter);
    }, 300);

    return () => clearTimeout(handle);
  }, [recipientNameFilter]);

  //const [orderByFilter, setOrderByFilter] = useState<string>();
  const [clientManagerFilter, setClientManagerFilter] = useState<string>();
  const [eventTypeFilter, setEventTypeFilter] = useState<TEventTypes>();

  const companies = useMemo(() => {
    return _.uniqBy(_.compact(events.map((e) => e.company)), 'id');
  }, [events]);

  const filterByCompany = useCallback(
    (e: TEventType) => {
      if (companyFilter && e.companyId !== companyFilter) return false;
      return true;
    },
    [companyFilter]
  );

  const companiesCounts = useMemo(
    () =>
      events.reduce<Record<string, number>>((acc, e) => {
        const { companyId = '0' } = e;
        acc[companyId] = (acc[companyId] || 0) + 1;
        return acc;
      }, {}),
    [events]
  );

  const months = useMemo(() => {
    return _.uniq(
      _.compact(
        events.map((e) => {
          return generateMonthFilter(e.eventDate);
        })
      )
    );
  }, [events]);

  const filterByMonth = useCallback(
    (e: TEventType) => {
      if (monthFilter && generateMonthFilter(e.eventDate) !== monthFilter) return false;
      return true;
    },
    [monthFilter]
  );

  const offices = useMemo(() => {
    return _.uniq(_.compact(events.filter((e) => filterByCompany(e)).map((e) => e.employee?.office)));
  }, [events, filterByCompany]);

  const filterByOffice = useCallback(
    (e: TEventType) => {
      if (officeFilter && e.employee?.office?.id != officeFilter) return false;
      return true;
    },
    [officeFilter]
  );

  /* const filterOrderBy = useCallback(
    (e: TEventType[]) => {
      switch (orderByFilter) {
        case 'date_asc':
          return _.orderBy(e, ['eventDate'], ['asc']);
        case 'date_desc':
          return _.orderBy(e, ['eventDate'], ['desc']);
        case undefined:
        default:
          return e;
      }
    },
    [orderByFilter],
  ); */

  const filterByRecipientName = useCallback(
    (e: TEventType) => {
      if (recipientNameFilter != null && recipientNameFilter != '' && (e.recipient?.fullName == null || quickScore(e.recipient?.fullName, recipientNameFilter) < 0.7)) return false;

      return true;
    },
    [recipientNameFilter]
  );

  const filterByEventType = useCallback(
    (e: TEventType) => {
      if (eventTypeFilter && e.type != eventTypeFilter) return false;
      return true;
    },
    [eventTypeFilter]
  );

  const clientManagers = useMemo(() => {
    return _.unionBy(
      events.reduce<TManagerAccount[]>((acc, e) => {
        const manager = e.company?.accountManager;
        if (manager && typeof manager === 'string') acc.push({ name: manager, id: manager });

        return acc;
      }, []),
      'id'
    );
  }, [events]);

  const filterByClientManager = useCallback(
    (e: TEventType) => {
      if (clientManagerFilter && clientManagerFilter != e.company?.accountManager) return false;

      return true;
    },
    [clientManagerFilter]
  );

  const setCompany = useCallback(
    (companyId: any) => {
      setOfficeFilter(undefined);
      setCompanyFilter(companyId ? String(companyId) : undefined);
    },
    [setCompanyFilter]
  );

  const setMonth = useCallback(
    (month: any) => {
      setOfficeFilter(undefined);
      setMonthFilter(month ? String(month) : undefined);
    },
    [setMonthFilter]
  );

  const setOffice = useCallback((officeId: any) => {
    setOfficeFilter(officeId ? String(officeId) : undefined);
  }, []);

  return {
    companies,
    companiesCounts,
    companyFilter,
    setCompanyFilter,
    filterByCompany,
    setCompany,
    months,
    monthFilter,
    setMonthFilter,
    filterByMonth,
    setMonth,
    offices,
    officeFilter,
    setOfficeFilter,
    filterByOffice,
    setOffice,
    eventTypeFilter,
    setEventTypeFilter,
    filterByEventType,
    recipientNameFilter,
    setRecipientNameFilter,
    debouncedRecipientNameFilter,
    filterByRecipientName,
    /* orderByFilter,
    setOrderByFilter,
    filterOrderBy, */
    clientManagers,
    clientManagerFilter,
    setClientManagerFilter,
    filterByClientManager
  };
};

export const getSubRows = (events: TEventType[]) => {
  const subRowsIds: string[] = [];

  const enrichedEvents = _.orderBy(events, ['eventDate'], ['asc']).map((event) => {
    if (subRowsIds.includes(event.id)) return event;

    const item = _.cloneDeep(event);

    item.subRows = item.subRows ?? [];

    const itemDate = getDateWithTimezoneFixed(item.eventDate);
    if (!itemDate) return item;

    const subRows = events.filter((e) => {
      return e.addressId == item.addressId && e.id != item.id;
    });

    subRows.forEach((subRow) => {
      const row = _.omit(subRow, ['subRows']);
      const subRowEventDate = getDateWithTimezoneFixed(subRow.eventDate);
      if (!subRowEventDate) return;

      const diff = Math.abs(differenceInCalendarDays(itemDate, subRowEventDate));

      if (diff <= 14 && item.addressId == row.addressId)
        if (item.subRows) {
          //item.group = event.id;
          item.subRows.push({ ...row });
          subRowsIds.push(row.id);
        }
    });

    return item;
  });

  return enrichedEvents.filter((e) => !subRowsIds.includes(e.id));
};
